import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, Layers } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { API_BASE } from "../config";
import "../styles/vocabPrintPage.css";
import apiFetch from "../apiFetch";

const TIER_GROUPS = [
  {
    id: "1-4",
    label: "Tiers 1–4",
    badge: "Beginner",
    startTier: 1,
    endTier: 4,
    jumpColor: "linear-gradient(135deg,#14b8a6,#0d9488)",
  },
  {
    id: "5-9",
    label: "Tiers 5–9",
    badge: "Intermediate",
    startTier: 5,
    endTier: 9,
    jumpColor: "linear-gradient(135deg,#6366f1,#4f46e5)",
  },
  {
    id: "10-14",
    label: "Tiers 10–14",
    badge: "Advanced",
    startTier: 10,
    endTier: 14,
    jumpColor: "linear-gradient(135deg,#f97316,#ea580c)",
  },
  {
    id: "15-19",
    label: "Tiers 15–19",
    badge: "Expert",
    startTier: 15,
    endTier: 19,
    jumpColor: "linear-gradient(135deg,#a855f7,#9333ea)",
  },
  {
    id: "20+",
    label: "Tier 20+",
    badge: "Master",
    startTier: 20,
    endTier: null,
    jumpColor: "linear-gradient(135deg,#eab308,#ca8a04)",
  },
];

function formatList(arr) {
  if (!arr || arr.length === 0) return "—";
  return arr.join(", ");
}

export default function VocabPrintPage() {
  const [words, setWords] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    document.title = "Vocabulary Word List — Owllet Reading Quest";
    apiFetch(`/api/vocabulary`)
      .then((r) => r.json())
      .then((data) => {
        setWords(Array.isArray(data) ? data : data.words || []);
        setStatus("ok");
      })
      .catch(() => setStatus("error"));
  }, []);

  const handlePrintAll = () => {
    document.body.classList.remove("vcp-printing-single");
    document
      .querySelectorAll(".vcp-tier-section")
      .forEach((el) => el.classList.remove("vcp-active-tier"));
    window.print();
  };

  const handlePrintTier = (tierId) => {
    document
      .querySelectorAll(".vcp-tier-section")
      .forEach((el) => el.classList.remove("vcp-active-tier"));
    document
      .querySelector(`[data-tier-id="${tierId}"]`)
      ?.classList.add("vcp-active-tier");
    document.body.classList.add("vcp-printing-single");
    window.print();
    window.addEventListener(
      "afterprint",
      () => {
        document.body.classList.remove("vcp-printing-single");
        document
          .querySelectorAll(".vcp-tier-section")
          .forEach((el) => el.classList.remove("vcp-active-tier"));
      },
      { once: true },
    );
  };

  if (status === "loading") {
    return (
      <div className="vcp-page">
        <PageHeader
          pageTitle="Vocabulary Word List"
          pageSubtitle="All words grouped by tier"
        />
        <div className="vcp-loading">
          <p className="vcp-loading-text">Loading vocabulary words…</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="vcp-page">
        <PageHeader
          pageTitle="Vocabulary Word List"
          pageSubtitle="All words grouped by tier"
        />
        <div className="vcp-loading">
          <p className="vcp-loading-text">Failed to load vocabulary words.</p>
          <Link to="/vocab-test/setup" className="vcp-back-btn">
            <ArrowLeft size={15} /> Back
          </Link>
        </div>
      </div>
    );
  }

  const totalWords = words.length;

  // Pre-compute which tier groups actually have words (for jump links)
  const activeGroups = TIER_GROUPS.filter((group) =>
    words.some(
      (w) =>
        w.tier >= group.startTier &&
        (group.endTier === null || w.tier <= group.endTier),
    ),
  );

  return (
    <div className="vcp-page">
      <PageHeader
        pageTitle="Vocabulary Word List"
        pageSubtitle="All words grouped by tier"
      />

      {/* ── Fixed top bar ── */}
      <div className="vcp-topbar">
        <div className="vcp-topbar-inner">
          <div className="vcp-tier-jumps">
            {activeGroups.map((group) => (
              <a
                key={group.id}
                href={`#tier-${group.id}`}
                className="vcp-jump-link"
                style={{ background: group.jumpColor }}
              >
                {group.label}
              </a>
            ))}
          </div>

          <div className="vcp-topbar-right">
            <span className="vcp-total-count">
              {totalWords.toLocaleString()} words
            </span>
            <button className="vcp-print-all-btn" onClick={handlePrintAll}>
              <Printer size={15} /> Print All Tiers
            </button>
          </div>
        </div>
      </div>

      <div className="vcp-body">
        {/* ── Print-only header (shown only when printing) ── */}
        <div className="vcp-print-header">
          <p className="vcp-print-title">
            Owllet Reading Quest — Vocabulary Word List
          </p>
        </div>

        {/* ── Tier sections ── */}
        {TIER_GROUPS.map((group) => {
          const groupWords = words
            .filter(
              (w) =>
                w.tier >= group.startTier &&
                (group.endTier === null || w.tier <= group.endTier),
            )
            .sort((a, b) => a.tier - b.tier || a.word.localeCompare(b.word));

          if (groupWords.length === 0) return null;

          return (
            <section
              key={group.id}
              id={`tier-${group.id}`}
              className="vcp-tier-section"
              data-tier-id={group.id}
            >
              <div className="vcp-tier-header">
                <div className="vcp-tier-title">
                  <Layers size={16} />
                  <h2>{group.label}</h2>
                  <span className="vcp-tier-badge">{group.badge}</span>
                  <span className="vcp-tier-count">
                    {groupWords.length} words
                  </span>
                </div>
                <button
                  className="vcp-print-tier-btn"
                  onClick={() => handlePrintTier(group.id)}
                >
                  <Printer size={13} /> Print Tier
                </button>
              </div>

              <div className="adm-table-wrap vcp-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th className="vcp-col-num">#</th>
                      <th className="vcp-col-word">Word</th>
                      <th className="vcp-col-def">Definition</th>
                      <th className="vcp-col-syn">Synonyms</th>
                      <th className="vcp-col-ant">Antonyms</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupWords.map((w, i) => (
                      <tr key={w.id}>
                        <td className="adm-muted-cell vcp-num">{i + 1}</td>
                        <td>
                          <strong className="vcp-word">{w.word}</strong>
                        </td>
                        <td className="vcp-definition">{w.definition}</td>
                        <td className="vcp-syn">{formatList(w.synonyms)}</td>
                        <td className="vcp-ant">{formatList(w.antonyms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
